import { Chip } from '../../atoms/chip/chip.js';
import type { CaseSeverity } from '../../status-colors.js';

/**
 * SeverityChip — how urgent a repair is, as a pill.
 *
 * Corpus: `DB:2031-2032` (`sevKeys` and `sevColors` — urgent
 * red, high amber, normal green), drawn by the shared chip factory at
 * `DB:1841-1842` off the base palette at
 * `DB:1625`. It ships on a case at `DB:553`, the
 * static pill this component renders.
 *
 * A molecule rather than an atom: it renders the `Chip` atom, which
 * owns the pill geometry both chips used to share through a `.rp-chip` rule that
 * lived in `status-chip.css` and belonged to neither. Nothing about the shape is
 * this component's own, so `severity-chip.css` still declares nothing — what is
 * its own is the three-key mapping below, and `severity-chip.test.tsx` holds it
 * against `status-colors.ts` so the corpus table is not quietly kept twice.
 *
 * The report form's severity picker (`DB:1291-1295`) uses the
 * same three colours and is *not* this component: it is a `role="group"` of
 * `aria-pressed` buttons with a neutral resting state and a 1.5px border when
 * selected (`DB:2174`). It is an unbuilt catalogue entry, and
 * the `border` that `status-colors.ts` exports is for it.
 *
 * The label is a prop, never a lookup in here: colour alone never carries the
 * meaning, and the words a person reads come from a translation catalogue
 * — the corpus keeps this set at `I18N-APP:114`.
 */
export interface SeverityChipProps {
  readonly severity: CaseSeverity;
  readonly label: string;
}

/** `DB:2031-2032` — `sevColors`, in the corpus's own tones. */
export const severityTones = {
  urgent: 'red',
  high: 'amber',
  routine: 'green',
} as const satisfies Record<CaseSeverity, 'green' | 'red' | 'amber' | 'slate' | 'grey'>;

/**
 * A blank label renders nothing, for the reason `StatusChip` sets out in full:
 * a pill with no text conveys its severity by colour alone, which rule 5 of the
 * library contract forbids, and this component may not invent the word. `Chip`
 * enforces it now, in one place rather than in each caller.
 */
export function SeverityChip({ severity, label }: SeverityChipProps) {
  return <Chip label={label} tone={severityTones[severity]} />;
}
